// .store/objectStore.js
import { makeAutoObservable, runInAction } from 'mobx'
import i18nStore from './i18nStore'
import { config } from '../config'
import logger from '../utils/logger'
import apiClient from '../utils/apiClient'
import { registerTenantScopedReset } from '../tenancy/tenantScopedState'
import { replaceScopedObject } from '../tenancy/scopedObjectDto'

export class ObjectStore {
	categories = []
	jsonfiles = [] // Для хранения загруженных JSON файлов подкатегорий
	catalogStatus = 'idle'
	catalogCountry = null
	catalogError = ''
	catalogGeneration = 0
	catalogPromise = null

	// объекты
	objects = [] // Массив объектов
	currentPage = 1
	totalPages = 1 // Количество страниц
	isFetchingObjects = false

	objectsForRequest = [] // объекты для заявки
	currentPageRequest = 1
	totalPagesRequest = 1
	/* excludedObjectsFromRequest = [] // Исключенные объекты из заявки */

	isLoading = false
	isLoaded = false // флаг для отслеживания завершения загрузки

	idNewObject = -1 // id нового объекта, возвращается с сервера

	searchFilter = {
		saleType: '',
		activeCategoryId: '',
		selectedSubcategories: [],
	}

	constructor() {
		makeAutoObservable(this, { catalogPromise: false })
	}

	clearOrganizationState() {
		this.objects = []
		this.currentPage = 1
		this.totalPages = 1
		this.isFetchingObjects = false
		this.objectsForRequest = []
		this.currentPageRequest = 1
		this.totalPagesRequest = 1
		// Categories and their load state are global reference data, not tenant data.
		this.idNewObject = -1
		this.searchFilter = { saleType: '', activeCategoryId: '', selectedSubcategories: [] }
	}

	setCategories(fields) {
		this.categories = fields
	}

	setSaleType(value) {
		this.searchFilter.saleType = value
	}

	// Метод для установки активной категории для фильтрации
	setActiveCategoryId(value) {
		this.searchFilter.activeCategoryId = value
	}

	// Метод для установки выбранных подкатегорий для фильтрации
	setSelectedSubcategories(value) {
		this.searchFilter.selectedSubcategories = value
	}

	// Метод для поиска cardTitleRender по id
	getCardTitleRenderById(id) {
		for (const category of this.categories) {
			const subcategory = category.subcategories.find(sub => sub.id === id)
			if (subcategory) {
				return subcategory.cardTitleRender
			}
		}
		return null // Если id не найден
	}

	// Проверка наличия JSON файла в стейте
	findJsonForSubcategory(subcategoryId, fileName) {
		return this.jsonsubcategories.find(
			json => json.subcategoryId === subcategoryId && json.fileName === fileName
		)
	}

	async createObject(newObject) {
		logger.log('🔍 Перед отправкой на сервер. newObject:', JSON.stringify(newObject, null, 2))

		// Специальное логирование фотографий
		if (newObject.photos) {
			logger.log('📸 Фотографии перед отправкой:', newObject.photos)
			logger.log('📸 Количество фотографий:', newObject.photos.length)
		} else {
			logger.log('📸 Фотографии отсутствуют в объекте')
		}

		logger.log('newObject:', JSON.stringify(newObject, null, 2))

		const countryCodeISO = "RU"
		const subcategoryId = newObject.subcategoryId
		const categoryId = newObject.categoryId

		try {
			// Создаем копию объекта для отправки, чтобы гарантировать сохранность фотографий
			const objectToSend = {
				...newObject,
				countryCodeISO,
				subcategoryId,
				categoryId,
			}

			// Еще раз проверяем фотографии перед отправкой
			if (newObject.photos) {
				objectToSend.photos = newObject.photos;
				logger.log('📸 Фотографии добавлены в объект для отправки')
			}

			const response = await apiClient.post(`/add-new-object-parsed`, objectToSend)

			const responseObject = response.data
			logger.log('🔍 После отправки на сервер. responseObject:', responseObject)
			if (!responseObject.alreadyImported) this.objects.unshift(responseObject.object)
			this.idNewObject = responseObject.object.id
			logger.log('👓 Новый объект:', responseObject)
			return { success: true, data: responseObject, alreadyImported: Boolean(responseObject.alreadyImported) }
		} catch (error) {
			logger.error('Ошибка при добавлении нового объекта:', error)
			// Если ошибка связана с превышением лимита (код 422)
			if (error.response && error.response.status === 422) {
				const errorData = error.response.data
				return {
					success: false,
					error: errorData.message,
					currentCount: errorData.currentCount,
					limit: errorData.limit,
				}
			}
			// Для других ошибок
			return {
				success: false,
				error:
					error.response?.data?.message ||
					'Произошла ошибка при создании объекта',
			}
		}
	}

	// Global reference catalog, keyed by country, independent of tenant generation.
	fetchCategoriesAndFiles(country = i18nStore.currentCountry) {
		if (this.catalogCountry === country && this.catalogStatus === 'loaded') return Promise.resolve(true)
		if (this.catalogCountry === country && this.catalogPromise) return this.catalogPromise
		const generation = ++this.catalogGeneration
		this.catalogCountry = country
		this.catalogStatus = 'loading'
		this.catalogError = ''
		this.isLoading = true
		this.isLoaded = false
		this.catalogPromise = Promise.all([
			apiClient.get(`/predefined_data/${country}`),
			apiClient.get(`/predefined_data/${country}/jsonfiles`),
		]).then(([categoriesResponse, filesResponse]) => {
			if (generation !== this.catalogGeneration) return false
			if (!Array.isArray(categoriesResponse.data?.categories) || !Array.isArray(filesResponse.data)) {
				throw new Error('INVALID_PROPERTY_CATALOG')
			}
			runInAction(() => {
				this.categories = categoriesResponse.data.categories
				this.jsonfiles = filesResponse.data
				this.catalogStatus = 'loaded'
				this.isLoaded = true
			})
			return true
		}).catch(() => {
			if (generation !== this.catalogGeneration) return false
			runInAction(() => {
				this.catalogStatus = 'error'
				this.catalogError = 'Не удалось загрузить категории. Повторите попытку.'
			})
			return false
		}).finally(() => {
			if (generation !== this.catalogGeneration) return
			runInAction(() => {
				this.isLoading = false
				this.catalogPromise = null
			})
		})
		return this.catalogPromise
	}

	fetchCategories() {
		return this.fetchCategoriesAndFiles()
	}

	async editObject(objectId, patch) {
		logger.log(
			`Отправка запроса на сервер для изменения объекта с ID: ${objectId}`
		)
		logger.log(`Патч: ${JSON.stringify(patch)}`)

		try {
			const response = await apiClient.patch(`/objects/${objectId}`, patch)

			const editedObject = response.data
			this.objects = replaceScopedObject(this.objects, objectId, editedObject)
			logger.log('👓 editedObject replaced from server scope', editedObject)

			return editedObject
		} catch (error) {
			logger.error('Ошибка при изменении объекта:', error)
			throw error
		}
	}

	//  метод для отправки объекта
	async addNewObject(objectFields, countryCodeISO, subcategoryId) {

		try {
			// Находим категорию, к которой принадлежит подкатегория
			let categoryId = null

			for (const category of this.categories) {
				const foundSubcategory = category.subcategories.find(
					subcategory => subcategory.id === subcategoryId
				)

				if (foundSubcategory) {
					categoryId = category.id
					break
				}
			}

			logger.log('🔍 Перед отправкой на сервер. objectFields:', JSON.stringify(objectFields, null, 2))
			logger.log('🔍 Перед отправкой на сервер. countryCodeISO:', countryCodeISO)
			logger.log('🔍 Перед отправкой на сервер. subcategoryId:', subcategoryId)
			logger.log('🔍 Перед отправкой на сервер. categoryId:', categoryId)



			const response = await apiClient.post(`/add-new-object`, {
				...objectFields,
				countryCodeISO,
				subcategoryId,
				categoryId, // Добавляем найденный categoryId
			})

			const newObject = response.data
			logger.log('🔍 После отправки на сервер. newObject:', newObject)
			this.objects.unshift(newObject.object)
			this.idNewObject = newObject.object.id
			logger.log('👓 Новый объект:', newObject)
			return { success: true, data: newObject }
		} catch (error) {
			logger.error('Ошибка при добавлении нового объекта:', error)
			// Если ошибка связана с превышением лимита (код 422)
			if (error.response && error.response.status === 422) {
				const errorData = error.response.data
				return {
					success: false,
					error: errorData.message,
					currentCount: errorData.currentCount,
					limit: errorData.limit
				}
			}
			// Для других ошибок
			return {
				success: false,
				error: error.response?.data?.message || 'Произошла ошибка при создании объекта'
			}
		}
	}

	deleteObjectFromNewRequest(objectId) {
		this.objectsForRequest = this.objectsForRequest.filter(
			obj => obj.id !== objectId
		)
		//this.excludedObjectsFromRequest.push(objectId)
	}

	// удаление объекта
	async deleteObject(id) {
		logger.log(`🌟 ft678g Удаление объекта с ID: ${id}`)

		try {
			const response = await apiClient.delete(`/objects/${id}`)

			logger.log(`Сервер подтвердил удаление объекта с ID: ${id}`)
			this.objects = this.objects.filter(obj => obj.id !== id)
		} catch (error) {
			logger.error(`Ошибка удаления объекта:`, error)
		}
	}



	async fetchLastNewObject() {
		if (this.objects.length === 0) {
			logger.log('Нет объектов для обновления в fetchLastNewObject.');
			return;
		}

		// Мы все еще используем ID первого объекта, так как предполагаем,
		// что именно его данные нужно обновить (например, добавить public URL для фото)
		const objectToUpdateId = this.objects[0].id;
		logger.log(`Запрос на обновление данных для объекта с ID: ${objectToUpdateId} (fetchLastNewObject)`);

		try {
			const response = await apiClient.post('/objects/last-new', { id: objectToUpdateId });
			logger.log('Сервер ответил на запрос fetchLastNewObject:', response.data);

			// Проверяем успешность ответа и наличие объекта
			if (response.data && response.data.success && response.data.object) {
				const updatedObjectData = response.data.object;

				// Используем runInAction для обновления наблюдаемого свойства MobX
				runInAction(() => {
					// Находим индекс объекта в массиве (хотя мы ожидаем, что это будет 0)
					const index = this.objects.findIndex(obj => obj.id === objectToUpdateId);
					if (index !== -1) {
						// Заменяем старый объект новыми данными
						this.objects = replaceScopedObject(this.objects, objectToUpdateId, updatedObjectData);
						logger.log(`Объект с ID ${objectToUpdateId} обновлен в MobX store.`);
					} else {
						logger.warn(`Объект с ID ${objectToUpdateId} не найден в MobX store после запроса fetchLastNewObject.`);
					}
				});
			} else {
				logger.error('Ошибка в ответе сервера fetchLastNewObject или отсутствуют данные объекта:', response.data);
			}
		} catch (error) {
			logger.error('Ошибка при отправке или обработке запроса fetchLastNewObject:', error);
			// Обработка ошибки
		}
	}
}

const objectStore = new ObjectStore()
registerTenantScopedReset('objects', () => objectStore.clearOrganizationState())
export default objectStore
